import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Heart, LogIn, LayoutDashboard } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import { getNavigationRoutes } from '../../config/routes';
import { useAuth } from '../../hooks/useAuth';
import { getAuthLabels, LOGIN_URL, CABINET_URL } from '../../config/app';

export default function Header() {
  const { t, language } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const authed = useAuth();
  const authLabels = getAuthLabels(language);
  const authHref = authed ? CABINET_URL : LOGIN_URL;
  const authText = authed ? authLabels.cabinet : authLabels.login;
  const AuthIcon = authed ? LayoutDashboard : LogIn;

  const navigationRoutes = getNavigationRoutes();
  const navigation = navigationRoutes.map((route) => ({
    name: t(`nav_${route.name}`),
    href: route.path === '/' ? '/' : route.path,
  }));

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-graweo-sand">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-10 h-10 bg-gradient-to-br from-graweo-terra to-graweo-chocolate rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
              <Heart className="w-5 h-5 text-white" fill="white" />
            </div>
            <span className="text-xl font-bold text-graweo-chocolate hidden sm:inline font-manrope">GRAWEO</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="px-4 py-2 text-sm font-medium text-graweo-chocolate hover:text-graweo-terra hover:bg-graweo-sand rounded-lg transition-colors"
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Right section */}
          <div className="flex items-center gap-4">
            <LanguageSwitcher />

            <a
              href={authHref}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-graweo-terra hover:bg-graweo-chocolate text-white font-semibold rounded-lg transition-all hover:scale-105"
            >
              <AuthIcon className="w-4 h-4" />
              <span>{authText}</span>
            </a>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-graweo-chocolate hover:bg-graweo-sand rounded-lg transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-graweo-sand">
            <div className="flex flex-col gap-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-sm font-medium text-graweo-chocolate hover:bg-graweo-sand rounded-lg transition-colors"
                >
                  {item.name}
                </Link>
              ))}
              <a
                href={authHref}
                onClick={() => setMobileMenuOpen(false)}
                className="sm:hidden mt-2 px-4 py-3 bg-graweo-terra hover:bg-graweo-chocolate text-white font-semibold rounded-lg text-center transition-colors"
              >
                {authText}
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}